import {Editor, type ListElement} from "slate";

import {
    getInlineCodeNode,
    unwrapInlineCodeNode,
    wrapInlineCodeNode,
} from "./slate/helpers/code-helpers.ts";
import {
    decreaseListDepth,
    increaseListDepth,
    isListNode,
    isSelectionWithinList,
} from "./slate/helpers/list-helpers.ts";
import {toggleMark} from "./slate/helpers/mark-helpers.ts";
import {
    isSelectionWithinTable,
    moveSelectionToNextCell,
    moveSelectionToPreviousCell,
    newParagraphAfterTable,
} from "./slate/helpers/table-helpers.ts";

export const handleKeyDown = (editor: Editor, event: KeyboardEvent) => {
    maybeHandle(event, {
        "Mod+b": () => {
            toggleMark(editor, "bold");
            return true;
        },
        "Mod+i": () => {
            toggleMark(editor, "italic");
            return true;
        },
        "Mod+u": () => {
            toggleMark(editor, "underline");
            return true;
        },
        "Mod+Shift+c": () => {
            if (getInlineCodeNode(editor)) {
                unwrapInlineCodeNode(editor);
            } else {
                wrapInlineCodeNode(editor);
            }
            return true;
        },
        "Mod+Enter": () => {
            if (isSelectionWithinTable(editor)) {
                newParagraphAfterTable(editor);
                return true;
            }
        },
        Enter: () => {
            if (isSelectionWithinTable(editor)) {
                Editor.insertText(editor, "\n");
                return true;
            }
        },
        "Shift+Enter": () => {
            Editor.insertText(editor, "\n");
            return true;
        },
        Tab: () => {
            if (isSelectionWithinTable(editor)) {
                moveSelectionToNextCell(editor);
                return true;
            }

            if (isSelectionWithinList(editor)) {
                const [node] = Editor.node(editor, editor.selection || [], {
                    depth: 1,
                });
                if (node && isListNode(node)) {
                    const listType = node.type;
                    if (
                        increaseListDepth(
                            editor,
                            editor.selection,
                            1,
                            listType as ListElement["type"],
                        )
                    ) {
                        return true;
                    }
                }
            }
        },
        "Shift+Tab": () => {
            if (isSelectionWithinTable(editor)) {
                moveSelectionToPreviousCell(editor);
                return true;
            }

            if (isSelectionWithinList(editor)) {
                if (decreaseListDepth(editor)) {
                    return true;
                }
            }
        },
    });
};

const maybeHandle = (
    event: KeyboardEvent,
    handlers: Record<string, () => void | boolean>,
): boolean => {
    const key = formatKey(event);
    if (handlers[key]) {
        const handled = !!handlers[key]();
        if (handled) {
            event.preventDefault();
            return true;
        }
    }
    return false;
};

const modPressed = (event: KeyboardEvent) => {
    const isMac =
        typeof window !== "undefined" &&
        /Mac|iPod|iPhone|iPad/.test(window.navigator.platform);

    return isMac ? event.metaKey : event.ctrlKey;
};

const formatKey = (event: KeyboardEvent) => {
    const mods: string[] = [];

    if (modPressed(event)) {
        mods.push("Mod");
    }

    if (event.shiftKey) {
        mods.push("Shift");
    }

    return [...mods, event.key].join("+");
};
